import React from "react";
import Header from "./Header";
const Service = () => {
  return (
    <div className="bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="relative">
        <div className="h-[60vh] relative overflow-hidden">
          {/* Background Image with Parallax Effect */}
          <div
            className="absolute inset-0 bg-cover bg-fixed bg-center"
            style={{ 
              backgroundImage: `url('/assets/images/others/coffee.jpg')`,
              transform: "translateZ(0)"
            }}
          ></div>

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-emerald-900/60"></div>

          {/* Content */}
          <Header />
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="max-w-3xl text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight drop-shadow-lg animate-fadeIn">
                Our Services
              </h1>
              <div className="w-24 h-1 bg-emerald-500 mx-auto mb-8"></div>
              <p className="text-xl text-white font-light max-w-2xl mx-auto">
                Delivering Ethiopia's finest coffee to the world with quality, traceability, and sustainability at our core.
              </p>
            </div>
          </div>
          
          {/* Decorative Wave Divider */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
              <path fill="#ffffff" fillOpacity="1" d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Service Highlights - What We Offer */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 relative text-center">
              <span className="relative z-10">
                What We Offer
                <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-emerald-500"></span>
              </span>
            </h2>
            <p className="text-gray-600 text-lg text-center mb-12 max-w-3xl mx-auto">
              Comprehensive agricultural services designed to meet global market demands with Ethiopian excellence
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Global Coffee Export",
                  description:
                    "Exporting premium-grade oilseeds, cereals, spices, and coffee to a global clientele.",
                  pic: "/assets/images/others/global.jpg", // Replace with actual image path
                },
                {
                  title: "Coffee Cultivation",
                  description:
                    "Cultivating and producing high-quality oilseeds and cereals through our own agricultural endeavors.",
                  pic: "/assets/images/others/coffee-cultivation.webp", // Replace with actual pic path
                },
                {
                  title: "Strategic Sourcing",
                  description:
                    "Strategically sourcing high-quality agricultural products to meet market needs.",
                  pic: "/assets/images/others/strategic.png", // Replace with actual pic path
                },
                {
                  title: "Agro-Processing",
                  description:
                    "Developing and operating modern agro-processing plants to add value to our products.",
                  pic: "/assets/images/others/agro.jpeg", // Replace with actual pic path
                },
                {
                  title: "Agricultural Inputs",
                  description:
                    "Importing and distributing essential agricultural inputs.",
                  pic: "/assets/images/others/agroinput.jpeg", // Replace with actual pic path
                },
                {
                  title: "Agro-Tech Innovation",
                  description:
                    "Introducing and implementing advanced agricultural technologies.",
                  pic: "/assets/images/others/agrotech.jpeg", // Replace with actual pic path
                },
              ].map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={service.pic}
                      alt={`${service.title}`}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      onError={(e) => {e.target.src = './coffee.jpg'}}
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {service.title}
                    </h3>
                    <div className="w-10 h-1 bg-emerald-500 mb-4"></div>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 relative text-center">
              <span className="relative z-10">
                Agricultural Products
                <span className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-emerald-500"></span>
              </span>
            </h2>
            <p className="text-gray-600 text-lg text-center mb-12 max-w-3xl mx-auto">
              Explore our premium selection of Ethiopian agricultural treasures
            </p>
            
            <div className="space-y-8">
              {[
                {
                  name: "Premium Coffee",
                  details:
                    "Ethiopian coffee beans, globally exported for rich flavor. Such as Ethiopian Yirgacheffe Coffee, Sidamo Coffee, Harrar Coffee, Jimma Coffee, Limu Coffee",
                  pic: "/assets/images/others/coffee.jpg", // Replace with actual image path
                },
                {
                  name: "Oilseeds & Cereals",
                  details:
                    "High-quality oilseeds and cereals grown sustainably. Such as Ethiopian Sesame Seeds, Niger Seeds (Noug), Flax Seeds, Groundnuts (Peanuts)",
                  pic: "/assets/images/others/oils.jpeg", // Replace with actual pic path
                },
                {
                  name: "Spices",
                  details:
                    "Authentic Ethiopian spices for global culinary markets. Such as Ethiopian Berbere, Mitmita, Korarima (Cardamom),Ginger (Zinjibil),Turmeric (Erd),Black Cumin (Black Jeera/Kalo Azez),Rue (Tena Adam),Sacred Basil (Besobila),Ajwain (Netch Azmud),Long Pepper (Timiz)",
                  pic: "/assets/images/others/spices.jpeg", // Replace with actual pic path
                },
                {
                  name: "Cereals and Pulses",
                  details:
                    "Ethiopian coffee beans, globally exported for rich flavor. Such as •	Ethiopian Teff, Barley, Wheat, Maize (Corn),  Sorghum, Chickpeas, Lentils, Haricot Beans, Fava Beans, Mung Beans, Soybeans",
                  pic: "/assets/images/others/cereal.jpeg", // Replace with actual pic path
                },
              ].map((product, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/4 h-full">
                      <img
                        src={product.pic}
                        alt={`${product.name}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {e.target.src = '/assets/images/others/coffee.jpg'}}
                      />
                    </div>
                    <div className="md:w-3/4 p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        {product.name}
                      </h3>
                      <div className="w-12 h-1 bg-emerald-500 mb-4"></div>
                      <p className="text-gray-600 leading-relaxed">
                        {product.details}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-emerald-700 py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Experience Our Products?
            </h2>
            <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
              Contact us today to explore our premium export services and bring
              the taste of Ethiopia to your market.
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-emerald-700 font-bold py-3 px-8 rounded-full hover:bg-emerald-50 transition-colors duration-300 shadow-lg"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Service;
